/**
 * Fuente única de verdad de los formatos de archivo aceptados por el wizard P1.
 *
 * Antes existían declaraciones divergentes: el dropzone anunciaba 9 formatos
 * (DBF/GeoJSON/KML/TSV incluidos) mientras que el guard de WizardPageV2 y el
 * parser real documentExtractor solo procesaban 5. Este módulo centraliza la
 * lista real para que el anuncio (chips + accept) y la validación de extensión
 * consuman una sola definición.
 *
 * Los formatos retirados (DBF, GeoJSON, KML, TSV) volverán al anunciarse junto
 * con su parser real (rebanada S1-b). No añadir una extensión aquí hasta que
 * documentExtractor sepa parsearla: el anuncio debe ser honesto.
 */

/** Extensiones realmente parseables por documentExtractor (sin punto, en minúsculas). */
export const ACCEPTED_EXTENSIONS = ['odt', 'xlsx', 'xls', 'ods', 'csv'] as const

export type AcceptedExtension = (typeof ACCEPTED_EXTENSIONS)[number]

/** Etiquetas de presentación para los chips del dropzone. */
export const FORMAT_TAGS = ['ODT', 'XLSX', 'XLS', 'ODS', 'CSV'] as const

/** Cadena `accept` para <input type="file">, derivada de las extensiones. */
export const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',')

/**
 * Texto legible para mensajes de error, con conjunción «y» (RAE):
 * «ODT, XLSX, XLS, ODS y CSV».
 */
export const ACCEPTED_FORMATS_LABEL = (() => {
  const tags = [...FORMAT_TAGS]
  if (tags.length <= 1) return tags.join('')
  return `${tags.slice(0, -1).join(', ')} y ${tags[tags.length - 1]}`
})()

/**
 * ¿La extensión (sin punto) corresponde a un formato soportado?
 * Acepta `undefined`/`null` para usarse directamente sobre el resultado de
 * `file.name.split('.').pop()`. Normaliza a minúsculas.
 */
export function isAcceptedExtension(
  ext: string | undefined | null,
): ext is AcceptedExtension {
  return !!ext && (ACCEPTED_EXTENSIONS as readonly string[]).includes(ext.toLowerCase())
}
